import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { formatLegislatorNameFromObject } from '../utils/nameUtils';
import { formatDistrict } from '../utils/districtDisplay';

// EntityOption interface - exported for use by consumers of these hooks
export interface EntityOption {
  id: string;
  display_name: string;
  additional_info?: string;
}

// Hook to fetch bills for selection - filters to workspace-tracked bills if workspaceId provided
export const useBillOptions = (searchTerm?: string, workspaceId?: string) => {
  return useQuery(
    ['bill-options', searchTerm, workspaceId],
    async () => {
      if (workspaceId) {
        // Fetch only workspace-tracked bills from workspace-bill-tracking endpoint
        const response = await api.get(`/workspace-bill-tracking/workspace/${workspaceId}`);
        let bills = response.data?.data?.bills || [];
        
        // Client-side filter by search term
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          bills = bills.filter((bill: any) => 
            (bill.billNumber || '').toLowerCase().includes(searchLower) ||
            (bill.title || '').toLowerCase().includes(searchLower)
          );
        }
        
        return bills.map((bill: any) => ({
          id: bill.billNumber || bill.id?.toString(), // Use bill number as ID for notes
          display_name: `${bill.billNumber || 'Unknown'} - ${bill.title || 'No title'}`,
          additional_info: bill.status || 'Tracked'
        }));
      } else {
        // Fallback to all bills (for backwards compatibility)
        const params = new URLSearchParams();
        if (searchTerm) {
          params.append('search', searchTerm);
        }
        params.append('limit', '50');
        
        const response = await api.get(`/data-management/bills?${params}`);
        const bills = response.data?.data?.bills || [];
        
        return bills.map((bill: any) => ({
          id: bill.number || bill.id,
          display_name: `${bill.number || 'Unknown'} - ${bill.title || 'No title'}`,
          additional_info: bill.latest_action || bill.status || 'No status'
        }));
      }
    },
    {
      enabled: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

// Hook to fetch legislators for selection - filters to workspace-tracked legislators if workspaceId provided
export const useLegislatorOptions = (searchTerm?: string, workspaceId?: string) => {
  return useQuery(
    ['legislator-options', searchTerm, workspaceId],
    async () => {
      try {
        if (workspaceId) {
          // Fetch only workspace-tracked legislators from workspace-legislator-tracking endpoint
          const response = await api.get(`/workspace-legislator-tracking/workspace/${workspaceId}`);
          let legislators = response.data?.data?.legislators || [];
          
          // Client-side filter by search term
          if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            legislators = legislators.filter((leg: any) => 
              (leg.fullName || leg.full_name || '').toLowerCase().includes(searchLower) ||
              (leg.district?.toString() || '').includes(searchLower)
            );
          }
          
          return legislators.map((legislator: any) => ({
            id: legislator.legislator_id?.toString() || legislator.id?.toString(),
            display_name: `${formatLegislatorNameFromObject(legislator)} (${formatDistrict(null, String(legislator.district ?? ''), legislator.chamber)})`,
            additional_info: legislator.partyAffiliation || legislator.party_affiliation
          }));
        } else {
          // Fallback to all legislators (for backwards compatibility)
          const params = new URLSearchParams();
          if (searchTerm) {
            params.append('search', searchTerm);
          }
          params.append('limit', '50');
          
          const response = await api.get(`/legislators?${params}`);
          const legislators = response.data?.data?.legislators || [];
          
          return legislators.map((legislator: any) => ({
            id: legislator.id.toString(),
            display_name: `${formatLegislatorNameFromObject(legislator)} (${formatDistrict(null, String(legislator.district ?? ''), legislator.chamber)})`,
            additional_info: legislator.party_affiliation
          }));
        }
      } catch (error) {
        console.error('Error fetching legislators:', error);
        throw error;
      }
    },
    {
      enabled: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    }
  );
};

// Hook to fetch calendar events for selection
export const useCalendarEventOptions = (searchTerm?: string) => {
  return useQuery(
    ['calendar-event-options', searchTerm],
    async () => {
      const params: any = {};
      if (searchTerm) {
        params.search = searchTerm;
      }
      params.limit = 50; // Limit for dropdown
      
      const response = await api.get('/calendar/events', { params });
      const events = response.data?.data?.events || [];
      
      return events.map((event: any) => {
        const date = event.reading_date ? new Date(event.reading_date).toLocaleDateString() : 'No Date';
        
        // Format time to be human-readable (e.g., "9:00 AM")
        let timeStr = '';
        if (event.reading_time) {
          const [hours, minutes] = event.reading_time.split(':');
          const hour = parseInt(hours, 10);
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
          timeStr = `${displayHour}:${minutes} ${ampm}`;
        }
        
        const dateTimeStr = timeStr ? `${date} ${timeStr}` : date;
        
        return {
          id: event.calendar_id?.toString() || event.id?.toString(),
          display_name: `${dateTimeStr} - ${event.reading_title || 'Untitled Event'}`,
          additional_info: event.room_number || event.chamber || 'No location'
        };
      });
    },
    {
      enabled: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );
};

// Hook to fetch budget items for selection - filters to workspace-tracked items if workspaceId provided
export const useBudgetItemOptions = (searchTerm?: string, workspaceId?: string) => {
  return useQuery(
    ['budget-item-options', searchTerm, workspaceId],
    async () => {
      const params: any = { limit: 50 };
      if (searchTerm) params.search = searchTerm;
      if (workspaceId) {
        params.workspace_id = workspaceId;
        params.tracked_only = 'true';
      }
      const response = await api.get('/budget/items', { params });
      const items = response.data?.data?.items || [];
      return items.map((item: any) => ({
        id: item.id?.toString(),
        display_name: `${item.item_number || ''} - ${item.item_title || 'Untitled'}`,
        additional_info: item.dept_name || item.status || ''
      }));
    },
    {
      enabled: true,
      staleTime: 5 * 60 * 1000,
    }
  );
};

// Hook to fetch ballot initiatives for selection - filters to workspace-tracked measures if workspaceId provided
export const useInitiativeOptions = (searchTerm?: string, workspaceId?: string) => {
  return useQuery(
    ['initiative-options', searchTerm, workspaceId],
    async () => {
      const displayName = (initiative: any): string =>
        initiative.popular_name || initiative.ballot_title || initiative.unofficial_caption ||
        initiative.sos_initiative_number || initiative.measure_number || 'Untitled measure';

      if (workspaceId) {
        // Fetch only workspace-tracked measures
        const response = await api.get(`/ballot-initiatives/workspace/${workspaceId}/tracking`);
        let initiatives = response.data?.data || [];

        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          initiatives = initiatives.filter((initiative: any) =>
            displayName(initiative).toLowerCase().includes(searchLower) ||
            (initiative.measure_number || '').toLowerCase().includes(searchLower) ||
            (initiative.sos_initiative_number || '').toLowerCase().includes(searchLower)
          );
        }

        return initiatives.map((initiative: any) => ({
          id: initiative.id,
          display_name: displayName(initiative),
          additional_info: initiative.election_calendar?.name || initiative.status || ''
        }));
      }

      // Fallback to the full directory
      const params: any = { limit: 50 };
      if (searchTerm) params.search = searchTerm;
      const response = await api.get('/ballot-initiatives', { params });
      const initiatives = response.data?.data || [];

      return initiatives.map((initiative: any) => ({
        id: initiative.id,
        display_name: displayName(initiative),
        additional_info: initiative.election_calendar?.name || initiative.status || ''
      }));
    },
    {
      enabled: true,
      staleTime: 5 * 60 * 1000,
    }
  );
};

// Main hook that returns the appropriate entity options based on entity type
// When workspaceId is provided, only returns entities tracked in that workspace
export const useEntityOptions = (entityType: string, searchTerm?: string, workspaceId?: string) => {
  const billQuery = useBillOptions(searchTerm, workspaceId);
  const legislatorQuery = useLegislatorOptions(searchTerm, workspaceId);
  const calendarQuery = useCalendarEventOptions(searchTerm);
  const budgetQuery = useBudgetItemOptions(searchTerm, workspaceId);
  const initiativeQuery = useInitiativeOptions(searchTerm, workspaceId);

  switch (entityType) {
    case 'bill':
      return billQuery;
    case 'legislator':
      return legislatorQuery;
    case 'calendar':
    case 'calendar_event':
      return calendarQuery;
    case 'budget_item':
      return budgetQuery;
    case 'ballot_initiative':
      return initiativeQuery;
    default:
      return { data: [], isLoading: false, error: null };
  }
};
